/**
 * ChecklistContainer — DnD Kit wrapper for checklist items.
 *
 * Provides:
 * - SortableContext for drag-and-drop reordering
 * - DragOverlay for smooth visual feedback
 * - Rich-text callbacks: split (Enter), merge (Backspace), delete
 * - **Pending-ops optimistic updates**: each add/delete is tracked
 *   independently by ID and auto-cleaned when real data catches up.
 *   This prevents the "setOptimistic(null) clears everything" flash.
 * - Progress summary (completed / total counter + bar)
 */

import {
  useState,
  useCallback,
  useMemo,
  useLayoutEffect,
  useReducer,
  useRef,
  useEffect,
} from "react"
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core"
import { contentZoomModifier } from "@/features/dnd/zoomModifier"
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import type { DbObject } from "@/db/schema"
import { ChecklistSubType, ObjectStatus, ObjectType } from "@/db/schema"
import {
  reorderChecklistItem,
  updateChecklistTitle,
  updateChecklistStatus,
  updateChecklistSubType,
  deleteChecklistItem,
  checklistParentPath,
  updateUserStatus,
} from "@/db/operations"
import { planChecklistInsert, planChecklistMove } from "@totodo/operations"
import type { ChecklistIndexChange } from "@totodo/operations"
import { mergeItems, mergeOverride } from "./mergeItems"
import { createTaskDefaults } from "@/shared/types/task"
import { now } from "@/shared/utils/date/dateUtils"
import { db } from "@/db/schema"
import { ChecklistItem } from "./ChecklistItem"
import { setCursorOffset, focusAtXPosition } from "./cursorUtils"
import { scrollChecklistRowIntoView } from "./scrollUtils"
import { ChecklistHeading } from "./ChecklistHeading"
import { ChecklistStatusMenu } from "./ChecklistStatusMenu"
import {
  SwipeableChecklistRow,
  type ChecklistSwipeAction,
} from "./SwipeableChecklistRow"
import type { ParsedLine } from "./parseClipboardLines"
import { ChecklistUndoStack, type UndoEntry } from "./undoStack"
// TODO: Re-enable when add-item button is wired to the footer\n// import PlusIcon from "@/shared/icons/totodoicons/stroke/plus.svg?react"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChecklistContainerProps {
  /** Real items from Dexie liveQuery (NOT optimistic). */
  realItems: DbObject[]
  taskId: string
  taskParentPath: string
  /** Parent task's realmId — inherited by new checklist items.
   *  Critical for shared projects: items must live in the same realm. */
  taskRealmId: string
  /** FIXED recurring template — disable checklist toggle (read-only circles) */
  readOnlyToggle?: boolean
}

// ---------------------------------------------------------------------------
// Pending cursor — TipTap-inspired pattern
//
// Keyboard handlers store the DESIRED cursor position in a ref.
// After React commits DOM changes, useLayoutEffect applies it
// synchronously before the browser paints — zero visual flash.
//
// If the target element isn't in the DOM yet (e.g., a pending add whose
// ChecklistItem hasn't mounted), rAF retries until it appears.
// ---------------------------------------------------------------------------

type PendingCursor = { id: string; offset: number; atEnd?: boolean }

// ---------------------------------------------------------------------------
// Debug logging for iOS checklist deletion
// ---------------------------------------------------------------------------
import { clLog } from "@/shared/debug/checklistDebugLog"

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ChecklistContainer({
  realItems,
  taskId,
  taskParentPath,
  taskRealmId,
  readOnlyToggle,
}: ChecklistContainerProps) {
  const [activeId, setActiveId] = useState<string | null>(null)

  // Status menu state
  const [statusMenu, setStatusMenu] = useState<{
    itemId: string
    status: ObjectStatus
    progress?: number
    userStatus?: string | number | null
    subType?: number
    position: { x: number; y: number }
  } | null>(null)

  // -----------------------------------------------------------------------
  // Selection state
  // -----------------------------------------------------------------------

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const selectedIdsRef = useRef(selectedIds)
  selectedIdsRef.current = selectedIds
  const focusedIdRef = useRef<string | null>(null)
  const lastActionIdRef = useRef<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  /** Anchor for Shift+Arrow range selection (the item that started the shift-select) */
  const shiftAnchorIdRef = useRef<string | null>(null)
  /** Moving end of Shift+Arrow range selection */
  const shiftExtendIdRef = useRef<string | null>(null)

  // -----------------------------------------------------------------------
  // Collapse state (UI-only, not persisted)
  // -----------------------------------------------------------------------

  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    new Set(),
  )

  // -----------------------------------------------------------------------
  // Synchronous pending-ops state
  //
  // Uses REFS (not useState) so mutations are visible INSTANTLY to
  // subsequent keystrokes — no waiting for React re-render batch.
  // A reducer counter triggers React re-renders when needed.
  // -----------------------------------------------------------------------

  const pendingAddsRef = useRef<Record<string, DbObject>>({})
  const pendingDeletesRef = useRef<Record<string, true>>({})
  const pendingOverridesRef = useRef<Record<string, Partial<DbObject>>>({})
  const realItemsRef = useRef(realItems)
  realItemsRef.current = realItems

  const [renderKey, bumpRender] = useReducer((x: number) => x + 1, 0)

  // Compute merged items. Recomputes when:
  // - realItems changes (DB caught up)
  // - bumpRender() is called (after synchronous ref mutation)
  // Auto-cleans pending ops whose effects the DB has already realized.
  const items = useMemo(() => {
    const adds = pendingAddsRef.current
    const dels = pendingDeletesRef.current
    const ovr = pendingOverridesRef.current
    // Cleanup realized ops (idempotent — safe for strict mode)
    for (const id of Object.keys(adds)) {
      if (realItems.some((i) => i.id === id)) delete adds[id]
    }
    for (const id of Object.keys(dels)) {
      // Only clean up if: (1) not in realItems AND (2) not in pendingAdds
      // If it's in pendingAdds, the add hasn't reached DB yet — keep the delete so
      // mergeItems can cancel both out (prevents ghost rows on rapid add+delete)
      if (!realItems.some((i) => i.id === id) && !adds[id]) {
        delete dels[id]
      }
    }
    // If an item is in both adds and dels, they cancel out — remove both
    for (const id of Object.keys(adds)) {
      if (dels[id]) {
        delete adds[id]
        delete dels[id]
      }
    }
    // Cleanup overrides once DB reflects the change
    for (const [id, patch] of Object.entries(ovr)) {
      const real = realItems.find((i) => i.id === id)
      if (real) {
        const allApplied = Object.entries(patch).every(
          ([k, v]) => (real as unknown as Record<string, unknown>)[k] === v,
        )
        if (allApplied) delete ovr[id]
      }
    }
    return mergeItems(realItems, adds, dels, ovr)
  }, [realItems, renderKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // Ref for synchronous access in callbacks (always up-to-date)
  const itemsRef = useRef(items)
  itemsRef.current = items

  // -----------------------------------------------------------------------
  // Pending cursor (TipTap-inspired)
  //
  // Callbacks set pendingCursorRef, then bumpRender(). The useLayoutEffect
  // fires after React commits all DOM mutations (including child mounts)
  // and applies focus + cursor position before the browser paints.
  // -----------------------------------------------------------------------

  const pendingCursorRef = useRef<PendingCursor | null>(null)

  // -----------------------------------------------------------------------
  // Undo/Redo stack
  // -----------------------------------------------------------------------

  const undoStackRef = useRef(new ChecklistUndoStack())
  // Flag: true after a structural op (indent/split/merge/delete/reorder),
  // false after text input. Controls whether ⌘Z intercepts or falls through.
  const lastStructuralOpRef = useRef(false)

  /** Push undo entry + mark last action as structural */
  const pushUndo = useCallback((entry: UndoEntry) => {
    undoStackRef.current.push(entry)
    lastStructuralOpRef.current = true
  }, [])

  useLayoutEffect(() => {
    const pending = pendingCursorRef.current
    if (!pending) return
    pendingCursorRef.current = null

    const apply = (attempt = 0) => {
      const el = document.querySelector(
        `[data-checklist-id="${pending.id}"] [contenteditable]`,
      ) as HTMLElement | null

      if (el) {
        // preventScroll: the browser's own "scroll focused element into
        // view" is what made the card lurch. Measured on Android, within
        // 90ms of Enter it moved scrollTop by ~240px and left the row 192px
        // above the fold, when 18px would have done.
        // scrollChecklistRowIntoView handles it minimally instead.
        el.focus({ preventScroll: true })
        if (pending.atEnd) {
          // Cursor at the end of text content
          const range = document.createRange()
          const sel = window.getSelection()
          range.selectNodeContents(el)
          range.collapse(false)
          sel?.removeAllRanges()
          sel?.addRange(range)
        } else {
          setCursorOffset(el, pending.offset)
        }
        // Scroll the newly-focused row into view — the card may have
        // grown (paste, add, convert) and the target can slip below fold.
        scrollChecklistRowIntoView(pending.id)
      } else if (attempt < 12) {
        // Element not yet mounted (e.g., pending add) — retry next frame
        requestAnimationFrame(() => apply(attempt + 1))
      }
    }

    apply()
  })

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  )

  // Sorted IDs for SortableContext
  const sortedIds = useMemo(() => items.map((i) => i.id!), [items])

  // Active item for DragOverlay
  const activeItem = useMemo(
    () => (activeId ? items.find((i) => i.id === activeId) : null),
    [activeId, items],
  )

  // -----------------------------------------------------------------------
  // Reorder: apply planChecklistMove's changes (the shared move rule, also
  // behind reorder_checklist_item). Usually one new fractional index; when
  // the neighbours leave no room, the whole list renumbered.
  // -----------------------------------------------------------------------

  const applyIndexChanges = useCallback((changes: ChecklistIndexChange[]) => {
    if (changes.length === 0) return
    // Optimistic: apply new indexes immediately to prevent snap-back flicker
    for (const { id, index } of changes) {
      pendingOverridesRef.current = mergeOverride(pendingOverridesRef.current, id, { index })
    }
    bumpRender()

    // Persist atomically
    db.transaction("rw", db.totodo, async () => {
      for (const { id, index } of changes) {
        await reorderChecklistItem(id, index)
      }
    })
  }, [])

  /**
   * Indexes for `count` new rows at `position` in the list as shown, by the shared insert rule
   * (planChecklistInsert, the one addChecklistItem applies). When the neighbours leave no room
   * for an index between them, the list is renumbered first, applied like a reorder.
   */
  const insertIndexes = useCallback(
    (position: number, count = 1): number[] => {
      const { indexes, changes } = planChecklistInsert(itemsRef.current, position, count)
      applyIndexChanges(changes)
      return indexes
    },
    [applyIndexChanges],
  )

  // -----------------------------------------------------------------------
  // DnD handlers
  // -----------------------------------------------------------------------

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const id = event.active.id as string
    setActiveId(id)
    // If dragging a non-selected item, clear selection
    if (selectedIdsRef.current.size > 0 && !selectedIdsRef.current.has(id)) {
      setSelectedIds(new Set())
    }
  }, [])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) {
        setActiveId(null)
        return
      }

      const currentItems = itemsRef.current
      const sel = selectedIdsRef.current

      // ── Multi-drag: reorder all selected items together ──
      if (sel.size > 1 && sel.has(active.id as string)) {
        const without = currentItems.filter((i) => !sel.has(i.id!))

        // Insert position in the "without" list
        const overOrigIdx = currentItems.findIndex((i) => i.id === over.id)
        let insertIdx: number
        if (sel.has(over.id as string)) {
          // Dropping over a selected item — between its nearest non-selected neighbors
          insertIdx = currentItems
            .slice(0, overOrigIdx)
            .filter((i) => !sel.has(i.id!)).length
        } else {
          // Dropping over a non-selected item
          const overInWithout = without.findIndex((i) => i.id === over.id)
          const activeOrigIdx = currentItems.findIndex(
            (i) => i.id === active.id,
          )
          insertIdx =
            overOrigIdx > activeOrigIdx ? overInWithout + 1 : overInWithout
        }

        applyIndexChanges(
          planChecklistMove(currentItems, [...sel], insertIdx),
        )
        setActiveId(null)
        return
      }

      // ── Single-item drag ──
      const fromIndex = currentItems.findIndex((i) => i.id === active.id)
      const toIndex = currentItems.findIndex((i) => i.id === over.id)
      if (fromIndex === -1 || toIndex === -1) {
        setActiveId(null)
        return
      }

      applyIndexChanges(
        planChecklistMove(currentItems, [active.id as string], toIndex),
      )
      setActiveId(null)
    },
    // reads from itemsRef + selectedIdsRef + pendingOverridesRef — no stale closure
    [applyIndexChanges],
  )

  const handleDragCancel = useCallback(() => {
    setActiveId(null)
  }, [])

  // -----------------------------------------------------------------------
  // Focus & selection callbacks
  // -----------------------------------------------------------------------

  /** When an item's contentEditable receives focus → clear selection */
  const handleFocusItem = useCallback((id: string) => {
    focusedIdRef.current = id
    lastActionIdRef.current = id
    shiftAnchorIdRef.current = null
    shiftExtendIdRef.current = null
    setSelectedIds(new Set())
  }, [])

  /** Ctrl/Shift click on a row → multi-select at item level */
  const handleSelectItem = useCallback(
    (id: string, modifiers: { meta: boolean; shift: boolean }) => {
      // Blur any focused contentEditable
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
      const prevFocused = focusedIdRef.current
      focusedIdRef.current = null

      setSelectedIds((prev) => {
        const next = new Set(prev)

        if (modifiers.shift && lastActionIdRef.current) {
          // Range select from last action to current
          const currentItems = itemsRef.current
          const lastIdx = currentItems.findIndex(
            (i) => i.id === lastActionIdRef.current,
          )
          const thisIdx = currentItems.findIndex((i) => i.id === id)
          if (lastIdx !== -1 && thisIdx !== -1) {
            const start = Math.min(lastIdx, thisIdx)
            const end = Math.max(lastIdx, thisIdx)
            for (let i = start; i <= end; i++) {
              const itemId = currentItems[i]?.id
              if (itemId) next.add(itemId)
            }
          }
        } else if (modifiers.meta) {
          // First Ctrl+click: also include the previously focused item
          if (next.size === 0 && prevFocused && prevFocused !== id) {
            next.add(prevFocused)
          }
          // Toggle
          if (next.has(id)) {
            next.delete(id)
          } else {
            next.add(id)
          }
        }

        return next
      })

      // Don't move anchor on shift-click (range extends from original anchor)
      if (!modifiers.shift) {
        lastActionIdRef.current = id
      }
    },
    [], // reads from refs
  )

  // -----------------------------------------------------------------------
  // Keyboard shortcuts: toggle complete / cancel
  // -----------------------------------------------------------------------

  /** ⌘K → Toggle Unfinished ↔ Completed */
  const handleToggleComplete = useCallback((itemId: string) => {
    const item = itemsRef.current.find((i) => i.id === itemId)
    if (!item) return
    const newStatus =
      item.status === ObjectStatus.Completed
        ? ObjectStatus.Unfinished
        : ObjectStatus.Completed
    // Optimistic override
    pendingOverridesRef.current = mergeOverride(pendingOverridesRef.current, itemId, {
      status: newStatus,
    })
    itemsRef.current = mergeItems(
      realItemsRef.current,
      pendingAddsRef.current,
      pendingDeletesRef.current,
      pendingOverridesRef.current,
    )
    bumpRender()
    updateChecklistStatus(itemId, newStatus)
  }, [])

  /** ⌘⇧H → Toggle Item ↔ Section */
  const handleToggleSectionHeading = useCallback((itemId: string, title?: string) => {
    const item = itemsRef.current.find((i) => i.id === itemId)
    if (!item) return
    const newSubType =
      item.subType === ChecklistSubType.Heading
        ? ChecklistSubType.Item
        : ChecklistSubType.Heading
    // Optimistic override (clear status + reset depth when converting to section)
    const overridePatch: Partial<DbObject> =
      newSubType === ChecklistSubType.Heading
        ? {
            subType: newSubType,
            status: ObjectStatus.Unfinished,
            progress: 0,
            userStatus: undefined,
            depth: 0,
            ...(title !== undefined ? { title } : {}),
          }
        : { subType: newSubType, ...(title !== undefined ? { title } : {}) }
    pendingOverridesRef.current = mergeOverride(pendingOverridesRef.current, itemId, overridePatch)
    itemsRef.current = mergeItems(
      realItemsRef.current,
      pendingAddsRef.current,
      pendingDeletesRef.current,
      pendingOverridesRef.current,
    )
    // Focus the same item after re-render (now it's a different component)
    pendingCursorRef.current = { id: itemId, offset: 0, atEnd: true }
    bumpRender()
    updateChecklistSubType(itemId, newSubType)
    if (title !== undefined) {
      updateChecklistTitle(itemId, title)
    }
  }, [])

  /** ⌥⌘K → Toggle Unfinished ↔ Canceled */
  const handleToggleCancel = useCallback((itemId: string) => {
    const item = itemsRef.current.find((i) => i.id === itemId)
    if (!item) return
    const newStatus =
      item.status === ObjectStatus.Canceled
        ? ObjectStatus.Unfinished
        : ObjectStatus.Canceled
    pendingOverridesRef.current = mergeOverride(pendingOverridesRef.current, itemId, {
      status: newStatus,
    })
    itemsRef.current = mergeItems(
      realItemsRef.current,
      pendingAddsRef.current,
      pendingDeletesRef.current,
      pendingOverridesRef.current,
    )
    bumpRender()
    updateChecklistStatus(itemId, newStatus)
  }, [])

  // -----------------------------------------------------------------------
  // Keyboard shortcuts: duplicate
  // -----------------------------------------------------------------------

  /** ⌘D → Duplicate item below */
  const handleDuplicateItem = useCallback(
    async (itemId: string) => {
      const currentItems = itemsRef.current
      const pos = currentItems.findIndex((i) => i.id === itemId)
      if (pos === -1) return
      const srcItem = currentItems[pos]!

      // Index between current and next (room made if there is none)
      const newIndexValue = insertIndexes(pos + 1)[0]!

      const newId = crypto.randomUUID()
      const timestamp = now()
      const parentPath = checklistParentPath(taskParentPath, taskId)

      const newItem = createTaskDefaults({
        id: newId,
        type: ObjectType.Checklist,
        subType: srcItem.subType,
        title: srcItem.title ?? "",
        status: srcItem.status,
        ...(srcItem.progress != null ? { progress: srcItem.progress } : {}),
        ...(srcItem.userStatus != null ? { userStatus: srcItem.userStatus } : {}),
        ...(srcItem.depth ? { depth: srcItem.depth } : {}),
        parentPath,
        realmId: taskRealmId,
        index: newIndexValue,
        createdAt: timestamp,
        updatedAt: timestamp,
      })

      // Undo: remove the duplicate
      pushUndo({
        updates: [],
        additions: [],
        removals: [newId],
        cursor: { id: itemId, offset: 0, atEnd: true },
      })

      // Optimistic add
      pendingAddsRef.current = {
        ...pendingAddsRef.current,
        [newId]: newItem as DbObject,
      }
      itemsRef.current = mergeItems(
        realItemsRef.current,
        pendingAddsRef.current,
        pendingDeletesRef.current,
        pendingOverridesRef.current,
      )
      bumpRender()

      // Focus the new item
      pendingCursorRef.current = { id: newId, offset: 0, atEnd: true }

      await db.totodo.add(newItem)
    },
    [taskId, taskParentPath, insertIndexes],
  )

  // -----------------------------------------------------------------------
  // Keyboard shortcuts: reorder up / down (⌘↑ / ⌘↓)
  // -----------------------------------------------------------------------

  /** Move one place up (-1) or down (+1); undo restores every index the move changed. */
  const reorderByOne = useCallback((itemId: string, delta: -1 | 1) => {
    const currentItems = itemsRef.current
    const pos = currentItems.findIndex((i) => i.id === itemId)
    if (pos < 0 || pos + delta < 0 || pos + delta >= currentItems.length) return

    const changes = planChecklistMove(currentItems, [itemId], pos + delta)
    if (changes.length === 0) return

    // Undo: restore the original indexes
    const original = new Map(currentItems.map((i) => [i.id!, i.index]))
    pushUndo({
      updates: changes.map(({ id }) => ({ id, data: { index: original.get(id) } })),
      additions: [],
      removals: [],
      cursor: { id: itemId, offset: 0, atEnd: true },
    })

    applyIndexChanges(changes)
  }, [pushUndo, applyIndexChanges])

  const handleReorderUp = useCallback(
    (itemId: string) => reorderByOne(itemId, -1),
    [reorderByOne],
  )

  const handleReorderDown = useCallback(
    (itemId: string) => reorderByOne(itemId, 1),
    [reorderByOne],
  )

  // -----------------------------------------------------------------------
  // Shift+Arrow selection
  // -----------------------------------------------------------------------

  /**
   * Shift+Arrow from a focused item → start or extend range selection.
   * The anchor stays fixed; subsequent calls move the extend-end.
   */
  const handleShiftArrow = useCallback(
    (id: string, direction: "up" | "down") => {
      const currentItems = itemsRef.current
      const idx = currentItems.findIndex((i) => i.id === id)
      if (idx === -1) return

      const nextIdx = direction === "down" ? idx + 1 : idx - 1
      if (nextIdx < 0 || nextIdx >= currentItems.length) return

      const nextId = currentItems[nextIdx]?.id
      if (!nextId) return

      // Blur the contentEditable so we're in "selection mode"
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
      focusedIdRef.current = null

      // First shift-arrow: set anchor to the starting item
      if (
        shiftAnchorIdRef.current === null ||
        selectedIdsRef.current.size === 0
      ) {
        shiftAnchorIdRef.current = id
      }
      shiftExtendIdRef.current = nextId

      // Calculate range from anchor to extend
      const anchorIdx = currentItems.findIndex(
        (i) => i.id === shiftAnchorIdRef.current,
      )
      if (anchorIdx === -1) return

      const start = Math.min(anchorIdx, nextIdx)
      const end = Math.max(anchorIdx, nextIdx)
      const newSelected = new Set<string>()
      for (let i = start; i <= end; i++) {
        const itemId = currentItems[i]?.id
        if (itemId) newSelected.add(itemId)
      }
      setSelectedIds(newSelected)
    },
    [],
  )

  // -----------------------------------------------------------------------
  // Multi-line paste → create checklist items
  // -----------------------------------------------------------------------

  /**
   * Multi-line paste: create items BELOW the anchor item.
   * Each ParsedLine becomes a checklist item or section with proper status.
   */
  const handlePasteItems = useCallback(
    async (anchorId: string, lines: ParsedLine[]) => {
      if (lines.length === 0) return

      const currentItems = itemsRef.current
      const anchorIdx = currentItems.findIndex((i) => i.id === anchorId)
      if (anchorIdx === -1) return

      // Indexes evenly between the anchor and the next item (room made if there is none)
      const indexes = insertIndexes(anchorIdx + 1, lines.length)

      const parentPath = checklistParentPath(taskParentPath, taskId)
      const timestamp = now()

      const statusMap: Record<string, ObjectStatus> = {
        completed: ObjectStatus.Completed,
        canceled: ObjectStatus.Canceled,
        unfinished: ObjectStatus.Unfinished,
      }

      const newItems: DbObject[] = []
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i]!
        const newId = crypto.randomUUID()

        const newItem = createTaskDefaults({
          id: newId,
          type: ObjectType.Checklist,
          subType:
            line.type === "section"
              ? ChecklistSubType.Heading
              : ChecklistSubType.Item,
          title: line.title,
          status:
            statusMap[line.status ?? "unfinished"] ?? ObjectStatus.Unfinished,
          parentPath,
          realmId: taskRealmId,
          index: indexes[i],
          createdAt: timestamp,
          updatedAt: timestamp,
        })
        newItems.push(newItem as DbObject)
      }

      // Optimistic adds
      for (const item of newItems) {
        pendingAddsRef.current = {
          ...pendingAddsRef.current,
          [item.id!]: item,
        }
      }
      itemsRef.current = mergeItems(
        realItemsRef.current,
        pendingAddsRef.current,
        pendingDeletesRef.current,
        pendingOverridesRef.current,
      )
      bumpRender()

      // Focus last created item
      const lastItem = newItems[newItems.length - 1]
      if (lastItem?.id) {
        pendingCursorRef.current = { id: lastItem.id, offset: 0, atEnd: true }
      }

      // Persist all in a single transaction
      await db.transaction("rw", db.totodo, async () => {
        for (const item of newItems) {
          await db.totodo.add(item)
        }
      })
    },
    [taskId, taskParentPath, insertIndexes],
  )

  // -----------------------------------------------------------------------
  // Keyboard shortcuts: delete selected, select all
  // -----------------------------------------------------------------------

  /** Delete all items in selection */
  const handleDeleteSelected = useCallback(() => {
    const sel = selectedIdsRef.current
    if (sel.size === 0) return

    // Find the first item AFTER the selection to focus
    const currentItems = itemsRef.current
    const selArr = [...sel]
    const lastSelIdx = Math.max(
      ...selArr.map((id) => currentItems.findIndex((i) => i.id === id)),
    )
    const nextFocus = currentItems.find(
      (i, idx) => idx > lastSelIdx && !sel.has(i.id!),
    )
    const prevFocus = [...currentItems]
      .reverse()
      .find(
        (i, _idx) =>
          currentItems.indexOf(i) <
            Math.min(
              ...selArr.map((id) =>
                currentItems.findIndex((it) => it.id === id),
              ),
            ) && !sel.has(i.id!),
      )

    // Undo: re-add all deleted items
    const deletedItems = currentItems.filter((i) => sel.has(i.id!))
    if (deletedItems.length > 0) {
      pushUndo({
        updates: [],
        additions: deletedItems.map((i) => ({ ...i })),
        removals: [],
        cursor: { id: deletedItems[0]!.id!, offset: 0 },
      })
    }

    // Optimistic deletes
    for (const id of sel) {
      pendingDeletesRef.current = {
        ...pendingDeletesRef.current,
        [id]: true as const,
      }
    }
    itemsRef.current = mergeItems(
      realItemsRef.current,
      pendingAddsRef.current,
      pendingDeletesRef.current,
      pendingOverridesRef.current,
    )
    bumpRender()
    setSelectedIds(new Set())

    // Focus next (or previous) unselected item
    const focusTarget = nextFocus ?? prevFocus
    if (focusTarget?.id) {
      pendingCursorRef.current = { id: focusTarget.id, offset: 0, atEnd: true }
    }

    // Persist
    db.transaction("rw", db.totodo, async () => {
      for (const id of sel) {
        await deleteChecklistItem(id)
      }
    })
  }, [])

  /** ⌘A → Select all checklist items */
  const handleSelectAll = useCallback(() => {
    const allIds = new Set(itemsRef.current.map((i) => i.id!))
    setSelectedIds(allIds)
  }, [])

  // -----------------------------------------------------------------------
  // Clear focusedIdRef when focus leaves the checklist container.
  // Without this, stale focusedIdRef causes shortcuts to fire
  // when the user is in the TipTap editor or title field.
  // -----------------------------------------------------------------------

  useEffect(() => {
    const onFocusIn = (e: FocusEvent) => {
      const container = containerRef.current
      if (
        container &&
        !container.contains(e.target as Node) &&
        focusedIdRef.current !== null
      ) {
        focusedIdRef.current = null
      }
    }
    document.addEventListener("focusin", onFocusIn)
    return () => document.removeEventListener("focusin", onFocusIn)
  }, [])

  // -----------------------------------------------------------------------
  // Undo / Redo
  // -----------------------------------------------------------------------

  /**
   * Apply an undo/redo entry: perform DB operations + optimistic updates.
   * Returns a "reverse entry" that can undo what was just applied.
   */
  const applyUndoEntry = useCallback(
    async (entry: UndoEntry): Promise<UndoEntry> => {
      const currentItems = itemsRef.current

      // Build the reverse entry (to undo this undo)
      const reverse: UndoEntry = {
        updates: [],
        additions: [],
        removals: [],
        cursor: pendingCursorRef.current
          ? { ...pendingCursorRef.current }
          : { id: focusedIdRef.current ?? entry.cursor.id, offset: 0, atEnd: true },
      }

      // Reverse of removals = additions (need to snapshot before removing)
      for (const id of entry.removals) {
        const item = currentItems.find((i) => i.id === id)
        if (item) reverse.additions.push({ ...item })
      }

      // Reverse of additions = removals
      for (const item of entry.additions) {
        reverse.removals.push(item.id!)
      }

      // Reverse of updates = store current values
      for (const { id, data } of entry.updates) {
        const item = currentItems.find((i) => i.id === id)
        if (item) {
          const oldData: Partial<DbObject> = {}
          for (const key of Object.keys(data)) {
            ;(oldData as Record<string, unknown>)[key] = (item as unknown as Record<string, unknown>)[key]
          }
          reverse.updates.push({ id, data: oldData })
        }
      }

      // Apply optimistically
      for (const id of entry.removals) {
        pendingDeletesRef.current = { ...pendingDeletesRef.current, [id]: true as const }
        // Clean up any pending add for this id
        const { [id]: _, ...restAdds } = pendingAddsRef.current
        pendingAddsRef.current = restAdds
      }
      for (const item of entry.additions) {
        pendingAddsRef.current = { ...pendingAddsRef.current, [item.id!]: item }
        // Remove from pending deletes if it was previously deleted
        const { [item.id!]: _, ...restDels } = pendingDeletesRef.current
        pendingDeletesRef.current = restDels
      }
      for (const { id, data } of entry.updates) {
        pendingOverridesRef.current = mergeOverride(pendingOverridesRef.current, id, data)
      }

      itemsRef.current = mergeItems(
        realItemsRef.current,
        pendingAddsRef.current,
        pendingDeletesRef.current,
        pendingOverridesRef.current,
      )
      pendingCursorRef.current = entry.cursor
      bumpRender()

      // Persist to DB
      await db.transaction("rw", db.totodo, async () => {
        for (const id of entry.removals) {
          await deleteChecklistItem(id)
        }
        for (const item of entry.additions) {
          // Use put to handle re-adding previously deleted items
          await db.totodo.put(item)
        }
        for (const { id, data } of entry.updates) {
          await db.totodo.update(id, { ...data, updatedAt: now() })
        }
      })

      return reverse
    },
    [],
  )

  const handleUndo = useCallback(async () => {
    const entry = undoStackRef.current.popUndo()
    if (!entry) return
    const reverse = await applyUndoEntry(entry)
    undoStackRef.current.pushRedo(reverse)
  }, [applyUndoEntry])

  const handleRedo = useCallback(async () => {
    const entry = undoStackRef.current.popRedo()
    if (!entry) return
    const reverse = await applyUndoEntry(entry)
    undoStackRef.current.pushUndo(reverse)
  }, [applyUndoEntry])

  // -----------------------------------------------------------------------
  // Indent / Outdent (Tab / Shift+Tab)
  // -----------------------------------------------------------------------

  const handleIndent = useCallback((itemId: string) => {
    const item = itemsRef.current.find((i) => i.id === itemId)
    if (!item) return
    const currentDepth = item.depth ?? 0
    if (currentDepth >= 3) return // max indent
    const newDepth = currentDepth + 1

    // Push undo
    pushUndo({
      updates: [{ id: itemId, data: { depth: currentDepth } }],
      additions: [],
      removals: [],
      cursor: { id: itemId, offset: 0, atEnd: true },
    })

    pendingOverridesRef.current = mergeOverride(pendingOverridesRef.current, itemId, {
      depth: newDepth,
    })
    itemsRef.current = mergeItems(
      realItemsRef.current,
      pendingAddsRef.current,
      pendingDeletesRef.current,
      pendingOverridesRef.current,
    )
    bumpRender()
    db.totodo.update(itemId, { depth: newDepth, updatedAt: now() })
  }, [])

  const handleOutdent = useCallback((itemId: string) => {
    const item = itemsRef.current.find((i) => i.id === itemId)
    if (!item) return
    const currentDepth = item.depth ?? 0
    if (currentDepth <= 0) return
    const newDepth = currentDepth - 1

    // Push undo
    pushUndo({
      updates: [{ id: itemId, data: { depth: currentDepth } }],
      additions: [],
      removals: [],
      cursor: { id: itemId, offset: 0, atEnd: true },
    })

    pendingOverridesRef.current = mergeOverride(pendingOverridesRef.current, itemId, {
      depth: newDepth,
    })
    itemsRef.current = mergeItems(
      realItemsRef.current,
      pendingAddsRef.current,
      pendingDeletesRef.current,
      pendingOverridesRef.current,
    )
    bumpRender()
    db.totodo.update(itemId, { depth: newDepth, updatedAt: now() })
  }, [])

  // -----------------------------------------------------------------------
  // Document-level keyboard handler (Escape, Delete, ⌘A)
  //
  // DOM containment guard: shortcuts only fire when the event target
  // is inside this checklist container, OR when items are selected.
  // This prevents spillover when the user is in the TipTap editor,
  // task title, or any other part of the page.
  // -----------------------------------------------------------------------

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // DOM containment guard — only process if the event originates
      // from within this checklist, or if items are actively selected.
      const target = e.target as HTMLElement
      const fromChecklist = containerRef.current?.contains(target) ?? false
      const hasSelection = selectedIdsRef.current.size > 0
      if (!fromChecklist && !hasSelection) return

      // Escape → clear selection (and stop propagation so TaskDetail
      // doesn't also close the detail view on the same keypress).
      if (e.key === "Escape" && selectedIdsRef.current.size > 0) {
        e.stopImmediatePropagation()
        shiftAnchorIdRef.current = null
        shiftExtendIdRef.current = null
        setSelectedIds(new Set())
        return
      }

      // ── ⌘Z / ⌘⇧Z → Undo / Redo structural operations ──
      // Guards:
      // 1. Must be from within the checklist (fromChecklist)
      // 2. Either NOT editing text, OR the last action was structural
      //    (so ⌘Z right after Tab/Enter/Delete undoes that, not text)
      // When editing text and lastStructural is false, let native undo handle it.
      const isEditingText =
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA")
      if ((e.key === "z" || e.key === "Z") && (e.metaKey || e.ctrlKey) && !e.altKey && fromChecklist) {
        const shouldIntercept = !isEditingText || lastStructuralOpRef.current
        if (!shouldIntercept) return // Let native undo/redo handle it
        if (e.shiftKey) {
          // Redo
          if (undoStackRef.current.canRedo()) {
            e.preventDefault()
            e.stopImmediatePropagation()
            handleRedo()
            lastStructuralOpRef.current = true
          }
        } else {
          // Undo — only intercept if we have structural ops in our stack
          if (undoStackRef.current.canUndo()) {
            e.preventDefault()
            e.stopImmediatePropagation()
            handleUndo()
            lastStructuralOpRef.current = true
          }
          // If stack is empty, let browser handle native text undo
        }
        return
      }

      // ── Shift+Arrow when items are selected → continue extending ──
      if (
        (e.key === "ArrowUp" || e.key === "ArrowDown") &&
        e.shiftKey &&
        selectedIdsRef.current.size > 0
      ) {
        const active = document.activeElement
        const isEditing =
          active instanceof HTMLElement &&
          (active.isContentEditable ||
            active.tagName === "INPUT" ||
            active.tagName === "TEXTAREA")
        if (!isEditing && shiftExtendIdRef.current) {
          e.preventDefault()
          e.stopImmediatePropagation()
          handleShiftArrow(
            shiftExtendIdRef.current,
            e.key === "ArrowDown" ? "down" : "up",
          )
          return
        }
      }

      // ── Arrow (no shift) with selection → clear selection + focus item ──
      if (
        (e.key === "ArrowUp" || e.key === "ArrowDown") &&
        !e.shiftKey &&
        selectedIdsRef.current.size > 0
      ) {
        const active = document.activeElement
        const isEditing =
          active instanceof HTMLElement &&
          (active.isContentEditable ||
            active.tagName === "INPUT" ||
            active.tagName === "TEXTAREA")
        if (!isEditing) {
          e.preventDefault()
          e.stopImmediatePropagation()
          const currentItems = itemsRef.current
          // Focus the first or last selected item depending on direction
          const selectedArr = currentItems.filter((i) =>
            selectedIdsRef.current.has(i.id!),
          )
          const target =
            e.key === "ArrowDown"
              ? selectedArr[selectedArr.length - 1]
              : selectedArr[0]
          shiftAnchorIdRef.current = null
          shiftExtendIdRef.current = null
          setSelectedIds(new Set())
          if (target?.id) {
            pendingCursorRef.current = {
              id: target.id,
              offset: 0,
              atEnd: e.key === "ArrowDown",
            }
            bumpRender()
          }
          return
        }
      }

      // Delete / Backspace (no modifier) → delete selected items
      if (
        (e.key === "Delete" || e.key === "Backspace") &&
        selectedIdsRef.current.size > 0
      ) {
        // Only fire when nothing is actively being edited
        const active = document.activeElement
        const isEditing =
          active instanceof HTMLElement &&
          (active.isContentEditable ||
            active.tagName === "INPUT" ||
            active.tagName === "TEXTAREA")
        if (!isEditing) {
          e.preventDefault()
          e.stopImmediatePropagation()
          handleDeleteSelected()
          return
        }
      }

      // ⌘A → select all checklist items
      // Two-stage: if editing text, first ⌘A selects all text in the field.
      // Only when all text is already selected does ⌘A select all items.
      if (
        e.key === "a" &&
        (e.metaKey || e.ctrlKey) &&
        !e.shiftKey &&
        !e.altKey
      ) {
        const inChecklist =
          focusedIdRef.current !== null || selectedIdsRef.current.size > 0
        if (inChecklist) {
          // Check if the active element is a contentEditable with partial selection
          const active = document.activeElement
          if (active instanceof HTMLElement && active.isContentEditable) {
            const sel = window.getSelection()
            const fullText = active.textContent ?? ""
            // Empty field → nothing to select, skip straight to select-all-items.
            // Non-empty → first ⌘A selects text, second ⌘A selects items.
            if (fullText.length > 0) {
              const allSelected =
                sel &&
                sel.rangeCount > 0 &&
                sel.toString().length >= fullText.length
              if (!allSelected) {
                // Let browser select all text within the field first
                return
              }
            }
            // All text already selected → fall through to select all items
            // Blur so the text selection is cleared
            active.blur()
          }
          e.preventDefault()
          e.stopImmediatePropagation()
          handleSelectAll()
          return
        }
      }

      // ⌘K → toggle complete on selected items
      if (
        e.key === "k" &&
        (e.metaKey || e.ctrlKey) &&
        !e.shiftKey &&
        !e.altKey &&
        selectedIdsRef.current.size > 0
      ) {
        e.preventDefault()
        e.stopImmediatePropagation()
        for (const id of selectedIdsRef.current) {
          handleToggleComplete(id)
        }
        return
      }

      // ⌥⌘K → toggle cancel on selected items
      if (
        e.code === "KeyK" &&
        (e.metaKey || e.ctrlKey) &&
        e.altKey &&
        !e.shiftKey &&
        selectedIdsRef.current.size > 0
      ) {
        e.preventDefault()
        e.stopImmediatePropagation()
        for (const id of selectedIdsRef.current) {
          handleToggleCancel(id)
        }
        return
      }
    }
    document.addEventListener("keydown", handler)
    return () => document.removeEventListener("keydown", handler)
  }, [
    handleDeleteSelected,
    handleSelectAll,
    handleToggleComplete,
    handleToggleCancel,
    handleShiftArrow,
    handleUndo,
    handleRedo,
  ])

  // Clear structural flag on text input inside checklist
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const onInput = () => { lastStructuralOpRef.current = false }
    container.addEventListener("input", onInput)
    return () => container.removeEventListener("input", onInput)
  }, [])

  // Whether we're in a multi-drag (for dimming)
  const isMultiDragging =
    activeId !== null && selectedIds.size > 1 && selectedIds.has(activeId)

  // -----------------------------------------------------------------------
  // Rich-text callbacks
  // -----------------------------------------------------------------------

  /**
   * Enter → split at cursor position.
   *
   * Two modes depending on cursor position:
   *
   * A) Cursor at END (textAfter is empty):
   *    Current item stays UNCHANGED — no reset, no animation.
   *    A new EMPTY item is created BELOW. Cursor moves there.
   *    This is the Word/Google Docs pattern.
   *
   * B) Cursor in MIDDLE (textAfter is non-empty):
   *    New item ABOVE with textBefore + inherited props.
   *    Current item becomes textAfter + reset to defaults.
   *    Focus stays in current item at offset 0.
   */
  const handleSplitAtCursor = useCallback(
    async (
      itemId: string,
      // The row's index; the new row's is planned from the list as shown (insertIndexes)
      _itemIndex: number,
      textBefore: string,
      textAfter: string,
    ) => {
      const currentItems = itemsRef.current
      const pos = currentItems.findIndex((i) => i.id === itemId)
      const currentItem = currentItems[pos]
      const newId = crypto.randomUUID()
      const timestamp = now()
      const parentPath = checklistParentPath(taskParentPath, taskId)

      if (textAfter === "") {
        // ─── Mode A: Cursor at end → new EMPTY item BELOW ────────────
        // Current item is untouched. New item gets default values, and an
        // index between it and the next item (room made if there is none).
        const newIndexValue = insertIndexes(pos + 1)[0]!

        const newItem = createTaskDefaults({
          id: newId,
          type: ObjectType.Checklist,
          subType: ChecklistSubType.Item,
          title: "",
          status: ObjectStatus.Unfinished,
          parentPath,
          realmId: taskRealmId,
          index: newIndexValue,
          createdAt: timestamp,
          updatedAt: timestamp,
          ...(currentItem?.depth ? { depth: currentItem.depth } : {}),
        })

        // Undo: remove the new item, cursor back to end of current
        pushUndo({
          updates: [],
          additions: [],
          removals: [newId],
          cursor: { id: itemId, offset: textBefore.length, atEnd: true },
        })

        // Synchronous — instantly visible
        pendingAddsRef.current = {
          ...pendingAddsRef.current,
          [newId]: newItem as DbObject,
        }
        itemsRef.current = mergeItems(
          realItemsRef.current,
          pendingAddsRef.current,
          pendingDeletesRef.current,
          pendingOverridesRef.current,
        )

        // Move cursor to the new item
        pendingCursorRef.current = { id: newId, offset: 0 }
        bumpRender()

        // Persist
        await db.totodo.add(newItem)
      } else {
        // ─── Mode B: Cursor in middle → split (new item ABOVE) ───────
        // Index between the previous item and this one (room made if there is none)
        const newIndexValue = insertIndexes(Math.max(0, pos))[0]!

        const inheritedStatus = currentItem?.status ?? ObjectStatus.Unfinished
        const inheritedSubType = currentItem?.subType ?? ChecklistSubType.Item
        const inheritedProgress = currentItem?.progress
        const inheritedUserStatus = currentItem?.userStatus

        const newItem = createTaskDefaults({
          id: newId,
          type: ObjectType.Checklist,
          subType: inheritedSubType,
          title: textBefore,
          status: inheritedStatus,
          ...(inheritedProgress != null ? { progress: inheritedProgress } : {}),
          ...(inheritedUserStatus != null ? { userStatus: inheritedUserStatus } : {}),
          parentPath,
          realmId: taskRealmId,
          index: newIndexValue,
          createdAt: timestamp,
          updatedAt: timestamp,
          ...(currentItem?.depth ? { depth: currentItem.depth } : {}),
        })

        // Undo: remove new item, restore current item's original state
        pushUndo({
          updates: [{
            id: itemId,
            data: {
              title: textBefore + textAfter,
              status: inheritedStatus,
              subType: inheritedSubType,
              ...(inheritedProgress != null ? { progress: inheritedProgress } : {}),
              ...(inheritedUserStatus != null ? { userStatus: inheritedUserStatus } : {}),
            },
          }],
          additions: [],
          removals: [newId],
          cursor: { id: itemId, offset: textBefore.length },
        })

        // Synchronous mutation
        pendingAddsRef.current = {
          ...pendingAddsRef.current,
          [newId]: newItem as DbObject,
        }
        // Merged into its pending override: when insertIndexes renumbered the list, that
        // holds this row's new index, and without it the two halves can show swapped
        pendingOverridesRef.current = mergeOverride(pendingOverridesRef.current, itemId, {
          title: textAfter,
          ...(inheritedStatus !== ObjectStatus.Unfinished
            ? { status: ObjectStatus.Unfinished }
            : {}),
          ...(inheritedSubType !== ChecklistSubType.Item
            ? { subType: ChecklistSubType.Item }
            : {}),
          ...(inheritedProgress != null ? { progress: 0 } : {}),
          ...(inheritedUserStatus != null ? { userStatus: undefined } : {}),
        })
        itemsRef.current = mergeItems(
          realItemsRef.current,
          pendingAddsRef.current,
          pendingDeletesRef.current,
          pendingOverridesRef.current,
        )

        // When subType changes (Section → Item), React swaps the component.
        if (inheritedSubType !== ChecklistSubType.Item) {
          pendingCursorRef.current = { id: itemId, offset: 0 }
        }

        bumpRender()

        // Persist atomically
        await db.transaction("rw", db.totodo, async () => {
          await updateChecklistTitle(itemId, textAfter)
          if (inheritedStatus !== ObjectStatus.Unfinished) {
            await updateChecklistStatus(itemId, ObjectStatus.Unfinished)
          }
          if (inheritedSubType !== ChecklistSubType.Item) {
            await updateChecklistSubType(itemId, ChecklistSubType.Item)
          }
          if (inheritedProgress != null) {
            await db.totodo.update(itemId, { progress: 0, updatedAt: timestamp })
          }
          if (inheritedUserStatus != null) {
            await db.totodo.update(itemId, { userStatus: undefined, updatedAt: timestamp })
          }
          await db.totodo.add(newItem)
        })
      }
    },
    [taskId, taskParentPath, insertIndexes], // reads from refs — no items dependency
  )

  /**
   * Backspace at pos 0 with text → absorb previous item (v1-style).
   *
   * The PREVIOUS item is deleted. Current item absorbs its text.
   * Focus stays in the current item — no focus() needed.
   *
   * Returns the previous item's title synchronously so ChecklistItem
   * can update its DOM + cursor immediately, or undefined if first item.
   */
  const handleMergeWithPrevious = useCallback(
    (id: string, text: string): string | undefined => {
      const currentItems = itemsRef.current
      const idx = currentItems.findIndex((i) => i.id === id)
      if (idx <= 0) return undefined

      const prevItem = currentItems[idx - 1]
      if (!prevItem?.id) return undefined

      const prevTitle = prevItem.title ?? ""
      const mergedText = prevTitle + text
      const prevStatus = prevItem.status ?? ObjectStatus.Unfinished
      const prevUserStatus = prevItem.userStatus
      const currentItem = currentItems[idx]

      // Undo: re-add previous item, restore current item's original text/status
      pushUndo({
        updates: [{
          id,
          data: {
            title: text,
            status: currentItem?.status ?? ObjectStatus.Unfinished,
            ...(currentItem?.userStatus != null ? { userStatus: currentItem.userStatus } : {}),
          },
        }],
        additions: [{ ...prevItem }],
        removals: [],
        cursor: { id, offset: 0 },
      })

      // Synchronous mutation — instantly visible to next keystroke
      pendingDeletesRef.current = {
        ...pendingDeletesRef.current,
        [prevItem.id!]: true as const,
      }
      // Optimistic override — merged title + inherited status/userStatus
      // visible before DB lands
      pendingOverridesRef.current = mergeOverride(pendingOverridesRef.current, id, {
        title: mergedText,
        ...(prevStatus !== ObjectStatus.Unfinished
          ? { status: prevStatus }
          : {}),
        ...(prevUserStatus != null ? { userStatus: prevUserStatus } : {}),
      })
      itemsRef.current = mergeItems(
        realItemsRef.current,
        pendingAddsRef.current,
        pendingDeletesRef.current,
        pendingOverridesRef.current,
      )
      bumpRender()

      // Persist atomically (fire and forget)
      db.transaction("rw", db.totodo, async () => {
        await updateChecklistTitle(id, mergedText)
        // Inherit deleted item's status
        if (prevStatus !== ObjectStatus.Unfinished) {
          await updateChecklistStatus(id, prevStatus)
        }
        // Inherit deleted item's userStatus (workflow color)
        if (prevUserStatus != null) {
          await updateUserStatus(id, prevUserStatus as string | undefined)
        }
        await deleteChecklistItem(prevItem.id!)
      })

      // Return prev title so ChecklistItem can update DOM synchronously
      return prevTitle
    },
    [], // reads from refs — no stale closure
  )

  /**
   * Delete (forward) at end of text → absorb NEXT item's text.
   *
   * The NEXT item is deleted. Current item appends its text.
   * Focus stays in current item — cursor stays at current position.
   *
   * Returns the next item's title synchronously, or undefined if last item.
   */
  const handleMergeWithNext = useCallback(
    (id: string, text: string): string | undefined => {
      const currentItems = itemsRef.current
      const idx = currentItems.findIndex((i) => i.id === id)
      if (idx < 0 || idx >= currentItems.length - 1) return undefined

      const nextItem = currentItems[idx + 1]
      if (!nextItem?.id) return undefined

      const nextTitle = nextItem.title ?? ""
      const mergedText = text + nextTitle

      // Undo: re-add next item, restore current item's original text
      pushUndo({
        updates: [{ id, data: { title: text } }],
        additions: [{ ...nextItem }],
        removals: [],
        cursor: { id, offset: text.length },
      })

      // Synchronous mutation
      pendingDeletesRef.current = {
        ...pendingDeletesRef.current,
        [nextItem.id!]: true as const,
      }
      pendingOverridesRef.current = mergeOverride(pendingOverridesRef.current, id, {
        title: mergedText,
      })
      itemsRef.current = mergeItems(
        realItemsRef.current,
        pendingAddsRef.current,
        pendingDeletesRef.current,
        pendingOverridesRef.current,
      )
      bumpRender()

      // Persist atomically
      db.transaction("rw", db.totodo, async () => {
        await updateChecklistTitle(id, mergedText)
        await deleteChecklistItem(nextItem.id!)
      })

      return nextTitle
    },
    [],
  )

  /**
   * Backspace on empty → delete + focus previous at end.
   */
  const handleDeleteAndFocusPrevious = useCallback(
    async (deletedId: string) => {
      const currentItems = itemsRef.current
      const idx = currentItems.findIndex((i) => i.id === deletedId)
      const prevItem = idx > 0 ? currentItems[idx - 1] : null
      const deletedItem = currentItems[idx]

      clLog("delete", `id=${deletedId.slice(-8)} idx=${idx} totalItems=${currentItems.length} prevId=${prevItem?.id?.slice(-8) ?? "none"}`)

      // Undo: re-add the deleted item, cursor back to it
      if (deletedItem) {
        pushUndo({
          updates: [],
          additions: [{ ...deletedItem }],
          removals: [],
          cursor: { id: deletedId, offset: 0 },
        })
      }

      // Synchronous mutation
      pendingDeletesRef.current = {
        ...pendingDeletesRef.current,
        [deletedId]: true as const,
      }
      itemsRef.current = mergeItems(
        realItemsRef.current,
        pendingAddsRef.current,
        pendingDeletesRef.current,
        pendingOverridesRef.current,
      )

      clLog("delete", `afterMerge: ${itemsRef.current.length} items, pendingDels=${Object.keys(pendingDeletesRef.current).length}`)

      // Set cursor BEFORE triggering re-render so useLayoutEffect picks it up
      if (prevItem?.id) {
        pendingCursorRef.current = { id: prevItem.id, offset: 0, atEnd: true }
      } else {
        // First item deleted — focus the next item (which is now at idx 0)
        const nextItem =
          idx < currentItems.length - 1 ? currentItems[idx + 1] : null
        if (nextItem?.id) {
          pendingCursorRef.current = { id: nextItem.id, offset: 0 }
        }
      }

      bumpRender()
      clLog("delete", `bumpRender called, awaiting deleteChecklistItem`)

      const result = await deleteChecklistItem(deletedId)
      clLog("delete", `deleteChecklistItem result: ${JSON.stringify(result)}`)
    },
    [], // reads from refs — no stale closure
  )

  // -----------------------------------------------------------------------
  // Other callbacks
  // -----------------------------------------------------------------------

  const handleOpenStatusMenu = useCallback(
    (itemId: string, anchorEl: HTMLElement) => {
      const item = itemsRef.current.find((i) => i.id === itemId)
      if (!item) return
      const rect = anchorEl.getBoundingClientRect()
      setStatusMenu({
        itemId,
        status: item.status,
        progress: item.progress ?? 0,
        userStatus: item.userStatus,
        subType: item.subType,
        position: { x: rect.left, y: rect.bottom + 4 },
      })
    },
    [], // reads from itemsRef — no stale closure
  )

  /** Swipe action handler — complete, indent/outdent, delete, headline, or menu */
  const handleSwipeAction = useCallback(
    (itemId: string, action: ChecklistSwipeAction) => {
      if (action === "complete") {
        handleToggleComplete(itemId)
      } else if (action === "indent") {
        handleIndent(itemId)
      } else if (action === "outdent") {
        handleOutdent(itemId)
      } else if (action === "delete") {
        handleDeleteAndFocusPrevious(itemId)
      } else if (action === "headline") {
        handleToggleSectionHeading(itemId)
      } else if (action === "toItem") {
        handleToggleSectionHeading(itemId)
      } else if (action === "collapse") {
        setCollapsedSections((prev) => {
          const next = new Set(prev)
          if (next.has(itemId)) {
            next.delete(itemId)
          } else {
            next.add(itemId)
          }
          return next
        })
      } else if (action === "menu") {
        const el = document.querySelector(
          `[data-checklist-id="${itemId}"]`,
        ) as HTMLElement | null
        if (el) {
          const rect = el.getBoundingClientRect()
          const item = itemsRef.current.find((i) => i.id === itemId)
          if (item) {
            setStatusMenu({
              itemId,
              status: item.status,
              progress: item.progress ?? 0,
              userStatus: item.userStatus,
              subType: item.subType,
              position: { x: rect.right - 200, y: rect.bottom + 4 },
            })
          }
        }
      }
    },
    [handleToggleComplete, handleIndent, handleOutdent, handleDeleteAndFocusPrevious, handleToggleSectionHeading],
  )

  // -----------------------------------------------------------------------
  // Arrow-key navigation callbacks
  //
  // Find the adjacent item's contentEditable DOM element and focus it,
  // preserving the horizontal (X) pixel position via caretRangeFromPoint.
  // -----------------------------------------------------------------------

  const handleNavigatePrevious = useCallback(
    (currentId: string, cursorX: number) => {
      const currentItems = itemsRef.current
      const idx = currentItems.findIndex((i) => i.id === currentId)
      if (idx <= 0) return
      const prevId = currentItems[idx - 1]!.id
      if (!prevId) return
      const el = document.querySelector(
        `[data-checklist-id="${prevId}"] [contenteditable]`,
      ) as HTMLElement | null
      if (el) focusAtXPosition(el, cursorX, "above")
    },
    [], // reads from itemsRef
  )

  const handleNavigateNext = useCallback(
    (currentId: string, cursorX: number) => {
      const currentItems = itemsRef.current
      const idx = currentItems.findIndex((i) => i.id === currentId)
      if (idx < 0 || idx >= currentItems.length - 1) return
      const nextId = currentItems[idx + 1]!.id
      if (!nextId) return
      const el = document.querySelector(
        `[data-checklist-id="${nextId}"] [contenteditable]`,
      ) as HTMLElement | null
      if (el) focusAtXPosition(el, cursorX, "below")
    },
    [], // reads from itemsRef
  )

  // TODO: Wire to footer add-item button (§12)
  // const handleAddItem = useCallback(async () => {
  //   const currentItems = itemsRef.current
  //   const lastItem =
  //     currentItems.length > 0
  //       ? currentItems[currentItems.length - 1]
  //       : undefined
  //   const lastIdx = lastItem?.index
  //
  //   const newId = crypto.randomUUID()
  //   const timestamp = now()
  //   const parentPath = checklistParentPath(taskParentPath, taskId)
  //   const newIndexValue = lastIdx !== undefined ? lastIdx + 1 : 0
  //
  //   const newItem = createTaskDefaults({
  //     id: newId,
  //     type: ObjectType.Checklist,
  //     title: "",
  //     parentPath,
  //     realmId: taskRealmId,
  //     index: newIndexValue,
  //     createdAt: timestamp,
  //     updatedAt: timestamp,
  //   })
  //
  //   // Synchronous mutation
  //   pendingAddsRef.current = {
  //     ...pendingAddsRef.current,
  //     [newId]: newItem as DbObject,
  //   }
  //   itemsRef.current = mergeItems(
  //     realItemsRef.current,
  //     pendingAddsRef.current,
  //     pendingDeletesRef.current,
  //     pendingOverridesRef.current,
  //   )
  //   bumpRender()
  //
  //   pendingCursorRef.current = { id: newId, offset: 0 }
  //
  //   // Persist
  //   await db.totodo.add(newItem)
  // }, [taskId, taskParentPath])

  // -----------------------------------------------------------------------
  // Progress summary
  // -----------------------------------------------------------------------

  // TODO: Wire to progress indicator in header (§12)
  // const completed = useMemo(
  //   () =>
  //     items.filter(
  //       (i) =>
  //         i.status === ObjectStatus.Completed ||
  //         i.status === ObjectStatus.Canceled,
  //     ).length,
  //   [items],
  // )
  // const total = items.length

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div className="py-2 pb-5" data-checklist-container ref={containerRef}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext
          items={sortedIds}
          strategy={verticalListSortingStrategy}
        >
          {items.map((item, idx) => {
            const itemId = item.id!
            const isSection = item.subType === ChecklistSubType.Heading

            // Skip items hidden by a collapsed section heading
            if (!isSection) {
              // Find the nearest preceding heading
              let headingId: string | undefined
              for (let i = idx - 1; i >= 0; i--) {
                const prev = items[i]
                if (prev?.subType === ChecklistSubType.Heading) {
                  headingId = prev.id!
                  break
                }
              }
              if (headingId && collapsedSections.has(headingId)) {
                return null
              }
            }

            if (isSection) {
              const isSel = selectedIds.has(itemId)
              const prevId = idx > 0 ? items[idx - 1]?.id : undefined
              const nextId =
                idx < items.length - 1 ? items[idx + 1]?.id : undefined
              const prevSelected = prevId ? selectedIds.has(prevId) : false
              const nextSelected = nextId ? selectedIds.has(nextId) : false

              return (
                <SwipeableChecklistRow
                  key={itemId}
                  onAction={(action) => handleSwipeAction(itemId, action)}
                  disabled={!!activeId}
                  isHeadline
                >
                <ChecklistHeading
                  id={itemId}
                  isDragActive={!!activeId}
                  optimisticItem={item}
                  startFocused={pendingCursorRef.current?.id === itemId}
                  isSelected={isSel}
                  isFirstSelected={isSel && !prevSelected}
                  isLastSelected={isSel && !nextSelected}
                  isDraggedAway={
                    isMultiDragging &&
                    selectedIds.has(itemId) &&
                    itemId !== activeId
                  }
                  onDeleteAndFocusPrevious={handleDeleteAndFocusPrevious}
                  onSplitAtCursor={handleSplitAtCursor}
                  onNavigatePrevious={handleNavigatePrevious}
                  onNavigateNext={handleNavigateNext}
                  onFocusItem={handleFocusItem}
                  onSelectItem={handleSelectItem}
                  onConvertToItem={handleToggleSectionHeading}
                  onShiftArrow={handleShiftArrow}
                  onPasteItems={handlePasteItems}
                  onIndent={handleIndent}
                  onOutdent={handleOutdent}
                  isCollapsed={collapsedSections.has(itemId)}
                />
                </SwipeableChecklistRow>
              )
            }

            const isSel = selectedIds.has(itemId)
            const prevId = idx > 0 ? items[idx - 1]?.id : undefined
            const nextId =
              idx < items.length - 1 ? items[idx + 1]?.id : undefined
            const prevSelected = prevId ? selectedIds.has(prevId) : false
            const nextSelected = nextId ? selectedIds.has(nextId) : false

            return (
              <SwipeableChecklistRow
                key={itemId}
                onAction={(action) => handleSwipeAction(itemId, action)}
                disabled={!!activeId}
                depth={item.depth ?? 0}
              >
              <ChecklistItem
                id={itemId}
                isDragActive={!!activeId}
                optimisticItem={item}
                startFocused={pendingCursorRef.current?.id === itemId}
                isSelected={isSel}
                isFirstSelected={isSel && !prevSelected}
                isLastSelected={isSel && !nextSelected}
                isDraggedAway={
                  isMultiDragging &&
                  selectedIds.has(itemId) &&
                  itemId !== activeId
                }
                readOnlyToggle={readOnlyToggle}
                onSplitAtCursor={handleSplitAtCursor}
                onMergeWithPrevious={handleMergeWithPrevious}
                onMergeWithNext={handleMergeWithNext}
                onDeleteAndFocusPrevious={handleDeleteAndFocusPrevious}
                onOpenStatusMenu={handleOpenStatusMenu}
                onNavigatePrevious={handleNavigatePrevious}
                onNavigateNext={handleNavigateNext}
                onFocusItem={handleFocusItem}
                onSelectItem={handleSelectItem}
                onToggleComplete={handleToggleComplete}
                onToggleCancel={handleToggleCancel}
                onConvertToSection={handleToggleSectionHeading}
                onDuplicateItem={handleDuplicateItem}
                onReorderUp={handleReorderUp}
                onReorderDown={handleReorderDown}
                onShiftArrow={handleShiftArrow}
                onPasteItems={handlePasteItems}
                onIndent={handleIndent}
                onOutdent={handleOutdent}
              />
              </SwipeableChecklistRow>
            )
          })}
        </SortableContext>

        {/* Drag overlay */}
        <DragOverlay dropAnimation={null} modifiers={[contentZoomModifier]}>
          {activeItem ? (
            activeItem.subType === ChecklistSubType.Heading ? (
              <ChecklistHeading id={activeItem.id!} isDragOverlay />
            ) : (
              <div className="relative">
                <ChecklistItem id={activeItem.id!} isDragOverlay />
                {isMultiDragging && (
                  <span
                    className="absolute -top-2 -right-2 flex items-center justify-center
                      w-5 h-5 rounded-full bg-primary text-primary-foreground
                      text-xs font-medium shadow-sm"
                  >
                    {selectedIds.size}
                  </span>
                )}
              </div>
            )
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Status menu (right-click on icon) */}
      {statusMenu && (
        <ChecklistStatusMenu
          itemId={statusMenu.itemId}
          currentStatus={statusMenu.status}
          currentProgress={statusMenu.progress}
          currentUserStatus={statusMenu.userStatus}
          currentSubType={statusMenu.subType}
          isCollapsed={collapsedSections.has(statusMenu.itemId)}
          position={statusMenu.position}
          onClose={() => setStatusMenu(null)}
          onToggleSectionHeading={handleToggleSectionHeading}
          onToggleCollapse={(id) => {
            setCollapsedSections((prev) => {
              const next = new Set(prev)
              if (next.has(id)) next.delete(id)
              else next.add(id)
              return next
            })
          }}
        />
      )}
    </div>
  )
}
